import React, { useState, ReactNode } from 'react';

export interface PseudoState {
  isHover: boolean;
  isActive: boolean;
}

interface PseudoProps {
  children: (activity: PseudoState) => ReactNode;
}

/**
 * # Pseudo
 *
 * Wrapper component that exposes ::hover and ::active state of the
 * mouse to its children.
 *
 * This is useful for components that want to have different styles
 * based on hover and active state. Used in-conjunction with p()
 * theme lookup to apply different styles based on the state.
 *
 * ### Usage:
 *
 *  ```
 *  <PseudoState>
 *   {({ isHovered, isActive }) => (
 *    <div
 *       style={combineStyles(
 *         styles.main,
 *
 *         p('chip', CONTAINER_PROPERTIES),
 *         isHovered && p('chipHovered', CONTAINER_PROPERTIES),
 *         isActive && p('chipActive', CONTAINER_PROPERTIES),
 *       )}
 *    >
 *     ...
 *    </div>
 *   )
 *  </PseudoState>
 *
 *  ```
 */
const Pseudo = ({ children }: PseudoProps) => {
  const [activity, setActivity] = useState<PseudoState>({
    isHover: false,
    isActive: false,
  });

  return (
    <div
      style={{ display: 'contents' }}
      onMouseEnter={() => setActivity(a => ({ ...a, isHover: true }))}
      onMouseLeave={() => setActivity({ isHover: false, isActive: false })}
      onMouseDown={() => setActivity(a => ({ ...a, isActive: true }))}
      onMouseUp={() => setActivity(a => ({ ...a, isActive: false }))}
    >
      {children(activity)}
    </div>
  );
};

export default Pseudo;
